import { z } from 'zod';
import { toVerifiedType } from '@/lib/twitter/verification.ts';
import { ensureDateOrNull } from '@/lib/utils/date-coercion';
import { readNumericMetric, toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TweetAuthor } from '@/types/tweet';

const dateInputSchema = z.union([z.string(), z.number(), z.date()]).nullish();
const publicMetricsSchema = z.record(z.string(), z.unknown());

const tweetAuthorRecordSchema = z
  .object({
    profileImageUrl: z.string().nullable().optional(),
    verifiedType: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
    createdAt: dateInputSchema,
    publicMetrics: publicMetricsSchema.nullish(),
  })
  .passthrough();

type TweetAuthorRecord = z.infer<typeof tweetAuthorRecordSchema> & {
  id?: unknown;
  username?: unknown;
  name?: unknown;
};

const parseTweetAuthorRecord = (value: unknown): TweetAuthorRecord | null => {
  const parsed = tweetAuthorRecordSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }
  return parsed.data;
};

const parsePublicMetricsRecord = (value: unknown): Record<string, unknown> | null => {
  const parsed = publicMetricsSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }
  return parsed.data;
};

export const extractPublicMetrics = (value: unknown): TweetAuthor['publicMetrics'] | undefined => {
  const metricsRecord = parsePublicMetricsRecord(value);
  if (!metricsRecord) {
    return;
  }
  const followersCount = readNumericMetric(metricsRecord, 'followersCount', 'followers_count');
  const followingCount = readNumericMetric(metricsRecord, 'followingCount', 'following_count');
  const tweetCount = readNumericMetric(metricsRecord, 'tweetCount', 'tweet_count');
  const listedCount = readNumericMetric(metricsRecord, 'listedCount', 'listed_count');
  if ([followersCount, followingCount, tweetCount, listedCount].every((metric) => typeof metric !== 'number')) {
    return;
  }
  return {
    followersCount,
    followingCount,
    tweetCount,
    listedCount,
  };
};

const resolveCreatedAt = (input: unknown): Date => {
  const parsed = dateInputSchema.safeParse(input);
  if (!parsed.success) {
    return new Date(0);
  }
  return ensureDateOrNull(parsed.data ?? null) ?? new Date(0);
};

export const buildTweetAuthorPreview = (value: unknown): TweetAuthor | null => {
  const author = parseTweetAuthorRecord(value);
  if (!author) {
    return null;
  }
  const id = toTrimmedNonEmptyString(author.id);
  const username = toTrimmedNonEmptyString(author.username);
  if (!(id && username)) {
    return null;
  }

  const nameValue = toTrimmedNonEmptyString(author.name) ?? username;
  const profileImageUrl = typeof author.profileImageUrl === 'string' ? author.profileImageUrl : undefined;
  const verifiedType = toVerifiedType(author.verifiedType);

  const metrics = extractPublicMetrics(author.publicMetrics ?? undefined);
  const publicMetrics = metrics
    ? {
        followersCount: metrics.followersCount,
        followingCount: metrics.followingCount,
        tweetCount: metrics.tweetCount,
        listedCount: metrics.listedCount,
      }
    : undefined;

  const preview = {
    id,
    username,
    name: nameValue,
    profileImageUrl,
    verifiedType,
    createdAt: resolveCreatedAt(author.createdAt),
    publicMetrics,
  } satisfies TweetAuthor;

  return preview;
};
